import React, { useState, useEffect } from 'react';
import { awardsAPI } from '../services/api';
import { Badge } from './ui/badge';
import {
  Award, Trophy, GraduationCap, Globe, Building, Star, Milestone
} from 'lucide-react';

const iconMap = {
  Award, Trophy, GraduationCap, Globe, Building, Star
};

const categoryColors = {
  Leadership: 'bg-teal-600',
  Award: 'bg-amber-500',
  Fellowship: 'bg-blue-600',
  Competition: 'bg-purple-600',
  Education: 'bg-indigo-600',
};

const AwardsTimeline = () => {
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    awardsAPI.getAll().then(data => { setAwards(data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <section id="awards-timeline" className="py-20 px-4 bg-gradient-to-b from-white via-amber-50/30 to-white" data-testid="awards-timeline-section">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-sm font-semibold mb-4 shadow-lg">
            <Trophy className="w-5 h-5" />
            <span>Career Journey</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            Awards & <span className="text-amber-600">Milestones</span>
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            A journey of academic excellence, international recognition, and dedicated service
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-400 via-teal-400 to-indigo-400"></div>

            <div className="space-y-8">
              {awards.map((award, index) => {
                const Icon = iconMap[award.icon] || Award;
                const color = categoryColors[award.category] || 'bg-gray-600';
                const isLeft = index % 2 === 0;

                return (
                  <div
                    key={award.id}
                    className={`relative flex items-start gap-4 md:gap-0 ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                    data-testid={`award-item-${award.id}`}
                  >
                    {/* Content */}
                    <div className={`flex-1 ml-14 md:ml-0 ${isLeft ? 'md:pr-12 md:text-right' : 'md:pl-12 md:text-left'}`}>
                      <div className={`bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-lg transition-shadow`}>
                        <div className={`flex items-center gap-2 mb-2 ${isLeft ? 'md:justify-end' : ''}`}>
                          <Badge className={`${color} text-white text-xs`}>{award.year}</Badge>
                          {award.category && (
                            <Badge variant="outline" className="text-xs border-gray-300 text-gray-500">{award.category}</Badge>
                          )}
                        </div>
                        <h3 className="font-bold text-gray-900 mb-1 text-sm md:text-base">{award.title}</h3>
                        {award.description && (
                          <p className="text-gray-600 text-sm">{award.description}</p>
                        )}
                      </div>
                    </div>

                    {/* Timeline node */}
                    <div className="absolute left-3 md:left-1/2 md:-translate-x-1/2 w-7 h-7 rounded-full bg-white border-2 border-amber-400 flex items-center justify-center shadow-md z-10">
                      <Icon className="w-3.5 h-3.5 text-amber-600" />
                    </div>

                    {/* Spacer for the other side */}
                    <div className="hidden md:block flex-1"></div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AwardsTimeline;
